import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    quickUpdateUrl: String,
    healthUrl: String,
    collapsed: { type: Boolean, default: false }
  }

  static targets = [
    "scoreValue", "scoreBadge", "scoreBar",
    "attentionSection", "attentionList", "attentionCount",
    "nudgeText", "collapseToggle",
    "percentComplete", "percentScheduled"
  ]

  connect() {
    // Store bound reference so removeEventListener works correctly
    this._boundOnGanttUpdate = this._onGanttUpdate.bind(this)
    document.addEventListener("schedule:task-updated-from-gantt", this._boundOnGanttUpdate)
  }

  disconnect() {
    document.removeEventListener("schedule:task-updated-from-gantt", this._boundOnGanttUpdate)
  }

  // ─── Actions ────────────────────────────────────────────────────────

  quickUpdate(event) {
    const btn = event.currentTarget
    const taskId = btn.dataset.taskId
    const percentDone = parseFloat(btn.dataset.percentDone)
    if (!taskId || isNaN(percentDone)) return

    // Optimistic UI: highlight selected chip
    const row = btn.closest("[data-task-row]")
    if (row) {
      row.querySelectorAll("[data-quick-chip]").forEach(c => {
        c.classList.remove("bg-blue-600", "text-white")
        c.classList.add("bg-gray-100", "text-gray-700")
      })
      btn.classList.remove("bg-gray-100", "text-gray-700")
      btn.classList.add("bg-blue-600", "text-white")

      // Update actual % display
      const actualEl = row.querySelector("[data-actual-pct]")
      if (actualEl) actualEl.textContent = `${Math.round(percentDone)}%`
    }

    // POST to quick_update
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.quickUpdateUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ task_id: taskId, percent_done: percentDone })
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        this._updateHealthUI(data.health)

        // Dispatch event for Gantt to pick up
        document.dispatchEvent(new CustomEvent("schedule:task-updated-from-health", {
          detail: { taskId: parseInt(taskId), percentDone }
        }))

        this._showToast("Progress updated", "notice")

        // Remove task row if it's no longer behind
        if (percentDone >= 100 || (data.health?.needs_attention && !this._taskInAttention(taskId, data.health.needs_attention))) {
          if (row) row.remove()
          this._updateAttentionCount(data.health.attention_count)
        }
      }
    })
    .catch(err => {
      console.error("Quick update failed:", err)
      this._showToast("Failed to update progress", "alert")
    })
  }

  toggleAttention() {
    this.collapsedValue = !this.collapsedValue
    if (this.hasAttentionSectionTarget) {
      this.attentionSectionTarget.classList.toggle("hidden", this.collapsedValue)
    }
    if (this.hasCollapseToggleTarget) {
      this.collapseToggleTarget.textContent = this.collapsedValue ? "▸" : "▾"
    }
  }

  refreshHealth() {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    fetch(this.healthUrlValue, {
      headers: { "X-CSRF-Token": csrfToken }
    })
    .then(r => r.json())
    .then(data => this._updateHealthUI(data))
    .catch(err => console.error("Health refresh failed:", err))
  }

  // ─── Private ────────────────────────────────────────────────────────

  _onGanttUpdate() {
    // Debounce health refresh after Gantt changes
    clearTimeout(this._refreshTimer)
    this._refreshTimer = setTimeout(() => this.refreshHealth(), 1000)
  }

  _updateHealthUI(health) {
    if (!health) return

    if (this.hasScoreValueTarget) {
      this.scoreValueTarget.textContent = health.score
    }

    if (this.hasScoreBadgeTarget) {
      const badge = this.scoreBadgeTarget
      badge.className = badge.className.replace(/bg-\w+-100 text-\w+-800/g, "")
      if (health.color === "green") {
        badge.classList.add("bg-green-100", "text-green-800")
      } else if (health.color === "yellow") {
        badge.classList.add("bg-yellow-100", "text-yellow-800")
      } else {
        badge.classList.add("bg-red-100", "text-red-800")
      }
    }

    if (this.hasPercentCompleteTarget) {
      this.percentCompleteTarget.textContent = `${health.percent_complete}%`
    }

    if (this.hasPercentScheduledTarget) {
      this.percentScheduledTarget.textContent = `${health.percent_scheduled}%`
    }

    this._updateAttentionCount(health.attention_count)

    if (health.nudge && this.hasNudgeTextTarget) {
      const days = health.nudge.last_updated_days_ago
      this.nudgeTextTarget.textContent = days != null
        ? `Last updated ${days === 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`}`
        : "No updates yet"
    }
  }

  _updateAttentionCount(count) {
    if (this.hasAttentionCountTarget) {
      this.attentionCountTarget.textContent = count ?? 0
    }
  }

  _taskInAttention(taskId, attentionGroups) {
    if (!attentionGroups) return false
    for (const group of Object.values(attentionGroups)) {
      if (group.some(t => t.id == taskId)) return true
    }
    return false
  }

  _showToast(message, type = "notice") {
    const cssClass = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    const toast = document.createElement("div")
    toast.className = cssClass
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.innerHTML = `
      <div class="flex items-center justify-between gap-2 px-4 py-3">
        <span class="text-[13px]">${message}</span>
        <button data-action="flash#dismiss" class="text-current opacity-70 hover:opacity-100">&times;</button>
      </div>
    `
    document.body.prepend(toast)
  }
}
